import { computed, ref } from 'vue';
import {
  firstHistoryArray,
  formatCurrency,
  formatNumber,
  numericValue,
  presentValue,
} from '../utils/qprecoFormatters';

/**
 * Derivações reativas do payload `detailed` da Área QPreço: identidade,
 * estimativa, similares, histórico de preço do imóvel e histórico do
 * condomínio.
 *
 * Extraído de `pages/AreaQprecoIndex.vue` mantendo cada cadeia de fallback
 * `snake_case`/`camelCase` na mesma ordem, porque o backend responde com as
 * duas convenções dependendo da fonte.
 */
export function useQprecoDetailData({
  detail,
  detailLoading,
  showCondominiumPanel,
}) {
  const showAllCondominiumHistory = ref(false);

  const detailInterest = computed(() => detail.value?.interest || {});
  const estimate = computed(
    () => detail.value?.estimate || detailInterest.value?.last_estimate || {}
  );
  const condominiumSummary = computed(() => detail.value?.condominium || {});
  const hasCondominiumSummary = computed(() =>
    Boolean(condominiumSummary.value?.id || condominiumSummary.value?.nome)
  );
  const condominiumLocation = computed(() =>
    [
      condominiumSummary.value?.bairro,
      condominiumSummary.value?.cidade,
      condominiumSummary.value?.uf,
    ]
      .filter(Boolean)
      .join(' · ')
  );
  const similarItems = computed(
    () => detail.value?.similar?.items || estimate.value?.comparables || []
  );
  const priceHistory = computed(() =>
    firstHistoryArray(
      detail.value?.price_history,
      detail.value?.priceHistory,
      detail.value?.interest?.price_history,
      detail.value?.interest?.priceHistory
    )
  );
  const priceHistoryMeta = computed(
    () =>
      detail.value?.price_history_meta ||
      detail.value?.priceHistoryMeta ||
      detail.value?.interest?.price_history_meta ||
      detail.value?.interest?.priceHistoryMeta ||
      {}
  );
  const priceHistoryQuery = computed(
    () =>
      detail.value?.price_history_query ||
      detail.value?.priceHistoryQuery ||
      detail.value?.interest?.price_history_query ||
      detail.value?.interest?.priceHistoryQuery ||
      {}
  );
  const priceHistoryCount = computed(
    () =>
      numericValue(priceHistoryMeta.value?.count) || priceHistory.value.length
  );
  const priceHistorySources = computed(() =>
    Array.isArray(priceHistoryMeta.value?.sources)
      ? priceHistoryMeta.value.sources
      : []
  );
  const priceHistoryQueryLabel = computed(() => {
    const query = priceHistoryQuery.value || {};
    const baseKey = presentValue(query.addressBaseKey, query.address_base_key);
    const address = presentValue(
      query.address,
      query.addressDetails,
      query.address_details,
      query.endereco,
      query.logradouro
    );
    const number = presentValue(
      query.streetNumber,
      query.street_number,
      query.numero,
      query.number
    );

    if (address && number && !String(address).includes(String(number))) {
      return `${address}, ${number}`;
    }

    if (address) return String(address);
    if (baseKey) return String(baseKey).replace(/::/g, ', nº ');
    return (
      presentValue(
        detailInterest.value?.addressDetails,
        detailInterest.value?.imovel_endereco,
        detailInterest.value?.address,
        detailInterest.value?.endereco
      ) || 'Endereço não informado'
    );
  });
  const isRentFlow = computed(() =>
    ['aluguel', 'temporada'].includes(detailInterest.value?.finalidade)
  );
  const condominiumPriceHistory = computed(() =>
    firstHistoryArray(
      detail.value?.condominium_price_history,
      detail.value?.condominiumPriceHistory,
      detail.value?.interest?.condominium_price_history,
      detail.value?.interest?.condominiumPriceHistory,
      detail.value?.condominium?.condominium_price_history,
      detail.value?.condominium?.condominiumPriceHistory,
      detail.value?.condominium?.price_history,
      detail.value?.condominium?.priceHistory
    )
  );
  const condominiumPriceHistoryMeta = computed(
    () =>
      detail.value?.condominium_price_history_meta ||
      detail.value?.condominiumPriceHistoryMeta ||
      detail.value?.interest?.condominium_price_history_meta ||
      detail.value?.interest?.condominiumPriceHistoryMeta ||
      detail.value?.condominium?.condominium_price_history_meta ||
      detail.value?.condominium?.condominiumPriceHistoryMeta ||
      {}
  );
  const condominiumPriceHistoryQuery = computed(
    () =>
      detail.value?.condominium_price_history_query ||
      detail.value?.condominiumPriceHistoryQuery ||
      detail.value?.interest?.condominium_price_history_query ||
      detail.value?.interest?.condominiumPriceHistoryQuery ||
      detail.value?.condominium?.condominium_price_history_query ||
      detail.value?.condominium?.condominiumPriceHistoryQuery ||
      {}
  );
  const condominiumPriceHistoryCount = computed(
    () =>
      numericValue(condominiumPriceHistoryMeta.value?.count) ||
      condominiumPriceHistory.value.length
  );
  const condominiumPriceHistoryStatus = computed(
    () =>
      condominiumPriceHistoryMeta.value?.status ||
      (condominiumPriceHistory.value.length ? 'ready' : 'empty')
  );
  const condominiumPriceHistoryLoading = computed(
    () => detailLoading.value && showCondominiumPanel.value
  );
  const condominiumPriceHistoryError = computed(() =>
    condominiumPriceHistoryStatus.value === 'error'
      ? condominiumPriceHistoryMeta.value?.error ||
        condominiumPriceHistoryMeta.value?.message ||
        'Não foi possível carregar o histórico de condomínio agora.'
      : ''
  );
  const condominiumPriceHistorySources = computed(() =>
    Array.isArray(condominiumPriceHistoryMeta.value?.sources)
      ? condominiumPriceHistoryMeta.value.sources
      : []
  );
  const hasCondominiumHistoryExternalSource = computed(() =>
    condominiumPriceHistory.value.some(
      item => item?.redacted || item?.external_source || item?.externalSource
    )
  );
  const condominiumPriceHistoryQueryLabel = computed(() => {
    const query = condominiumPriceHistoryQuery.value || {};
    const name = presentValue(
      query.condominiumName,
      query.condominium_name,
      query.nome,
      condominiumSummary.value?.nome
    );
    const address = presentValue(
      query.address,
      query.addressDetails,
      query.address_details,
      query.endereco,
      query.logradouro,
      condominiumSummary.value?.endereco
    );
    const number = presentValue(
      query.streetNumber,
      query.street_number,
      query.numero,
      query.number,
      condominiumSummary.value?.numero
    );
    const neighborhood = presentValue(
      query.neighborhood,
      query.bairro,
      condominiumSummary.value?.bairro
    );
    const city = presentValue(
      query.city,
      query.cidade,
      condominiumSummary.value?.cidade
    );
    const state = presentValue(
      query.state,
      query.uf,
      condominiumSummary.value?.uf
    );

    const addressWithNumber =
      address && number && !String(address).includes(String(number))
        ? `${address}, ${number}`
        : address;
    const location = [neighborhood, city, state].filter(Boolean).join(' · ');

    return (
      [name, addressWithNumber, location].filter(Boolean).join(' · ') ||
      priceHistoryQueryLabel.value
    );
  });
  const hasCondominiumSectionData = computed(
    () => hasCondominiumSummary.value || condominiumPriceHistory.value.length
  );
  const visibleCondominiumPriceHistory = computed(() =>
    showAllCondominiumHistory.value
      ? condominiumPriceHistory.value
      : condominiumPriceHistory.value.slice(0, 3)
  );
  const canExpandCondominiumPriceHistory = computed(
    () =>
      !showAllCondominiumHistory.value &&
      condominiumPriceHistory.value.length >
        visibleCondominiumPriceHistory.value.length
  );

  const condominiumStats = computed(() => [
    {
      key: 'available',
      label: 'Imóveis disponíveis',
      value: formatNumber(condominiumSummary.value?.listings_available),
    },
    {
      key: 'sale',
      label: 'Venda mediana',
      value: formatCurrency(condominiumSummary.value?.median_sale_price),
    },
    {
      key: 'rent',
      label: 'Aluguel mediano',
      value: formatCurrency(condominiumSummary.value?.median_rent_price),
    },
    {
      key: 'units',
      label: 'Unidades',
      value: condominiumSummary.value?.total_unidades
        ? formatNumber(condominiumSummary.value?.total_unidades)
        : 'Sem dados',
    },
  ]);

  return {
    showAllCondominiumHistory,
    detailInterest,
    estimate,
    condominiumSummary,
    hasCondominiumSummary,
    condominiumLocation,
    similarItems,
    priceHistory,
    priceHistoryMeta,
    priceHistoryCount,
    priceHistorySources,
    priceHistoryQueryLabel,
    isRentFlow,
    condominiumPriceHistory,
    condominiumPriceHistoryCount,
    condominiumPriceHistoryLoading,
    condominiumPriceHistoryError,
    condominiumPriceHistorySources,
    hasCondominiumHistoryExternalSource,
    condominiumPriceHistoryQueryLabel,
    hasCondominiumSectionData,
    visibleCondominiumPriceHistory,
    canExpandCondominiumPriceHistory,
    condominiumStats,
  };
}
